import {
  App,
  Button,
  Divider,
  Flex,
  Select,
  SelectProps,
  theme,
  Tooltip,
} from "antd";
import { useTranslation } from "react-i18next";

import { CellSelectWrap, DictOption } from "@/pages/Sales/style";
import { useContext, useEffect, useMemo, useState } from "react";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import useDataAdd from "@/hooks/useDataAdd";
import saleApi, { updateDic } from "@/apis/dict";
import useDataUpdate from "@/hooks/useDataUpdate";
import useDataDel from "@/hooks/useDataDel";
import { SConModal } from "@/styles/SConModal";
import { DictContext } from "@/layout/context/DictContext";
import DictForm from "./DictForm";
import { DictEnum, DictInfo, DictItem } from "@/types/dict";
import SText from "@/styles/SText";

interface SCountryProps extends SelectProps {
  type: DictEnum;
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  readOnly?: boolean;
}

const Dict = ({ type, value, onChange, readOnly, ...restProps }: SCountryProps) => {

  const onlyText = [DictEnum.BRAND, DictEnum.ADDRESS_MANAGE].includes(type);
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { token: { colorPrimary } } = theme.useToken();
  const {
    saleTypes, saleTradeTerm, salePayMethod, saleShippingMethod, saleCustomerType, purchaseTradeTerm, purchasePayMethod, address, exchangeRate, printSetting, brandData, purchaseRule, productType,
    setSaleTypes, setSaleTradeTerm, setSalePayMethod, setSaleShippingMethod, setSaleCustomerType, setPurchaseTradeTerm, setPurchasePayMethod, setAddress, setExchangeRate, setPrintSetting,
    setBrandData, setPurchaseRule, setProductType
  } = useContext(DictContext);

  const { add, loading: addLoading } = useDataAdd<[DictEnum, DictInfo], DictItem>({ api: saleApi.add });
  const { update, loading: upLoading } = useDataUpdate<[string, DictInfo]>({
    api: saleApi.update,
  });
  const { del } = useDataDel({ api: saleApi.del });

  const [val, setVal] = useState<string | string[] | undefined>(value || undefined);
  const [isShowAdd, setIsShowAdd] = useState<boolean>(false);
  const [detail, setDetail] = useState<DictItem | null>(null);

  useEffect(() => {
    setVal(value || undefined)
  }, [value])

  const info: Record<string, {
    list: Array<DictItem>;
    placeholder: string;
    onDel: (res: string) => void;
    onAdd: (res: DictItem) => void;
    onEdit: (res: DictItem) => void;
  }> = {
    [DictEnum.SALE_TYPE]: {
      list: saleTypes,
      placeholder: t("sale.plSaleTypes"),
      onDel: (res: string) =>
        setSaleTypes(saleTypes.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setSaleTypes([...saleTypes, res]),
      onEdit: (res: DictItem) =>
        setSaleTypes(
          saleTypes.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.SALE_PAY_METHOD]: {
      list: salePayMethod,
      placeholder: t("sale.plPaymentMethod"),
      onDel: (res: string) =>
        setSalePayMethod(salePayMethod.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setSalePayMethod([...salePayMethod, res]),
      onEdit: (res: DictItem) =>
        setSalePayMethod(
          salePayMethod.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.SALE_TRADE_TERM]: {
      list: saleTradeTerm,
      placeholder: t("sale.plTradeTerms"),
      onDel: (res: string) =>
        setSaleTradeTerm(saleTradeTerm.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setSaleTradeTerm([...saleTradeTerm, res]),
      onEdit: (res: DictItem) =>
        setSaleTradeTerm(
          saleTradeTerm.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.SALE_SHIPPING_METHOD]: {
      list: saleShippingMethod,
      placeholder: t("sale.plShipping"),
      onDel: (res: string) =>
        setSaleShippingMethod(saleShippingMethod.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setSaleShippingMethod([...saleShippingMethod, res]),
      onEdit: (res: DictItem) =>
        setSaleShippingMethod(
          saleShippingMethod.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.SALE_CUSTOMER_TYPE]: {
      list: saleCustomerType,
      placeholder: t("sale.plCustomerType"),
      onDel: (res: string) =>
        setSaleCustomerType(saleCustomerType.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setSaleCustomerType([...saleCustomerType, res]),
      onEdit: (res: DictItem) =>
        setSaleCustomerType(
          saleCustomerType.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.PURCHASE_TRADE_TERM]: {
      list: purchaseTradeTerm,
      placeholder: t("purchase.plTradeTerms"),
      onDel: (res: string) =>
        setPurchaseTradeTerm(purchaseTradeTerm.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setPurchaseTradeTerm([...purchaseTradeTerm, res]),
      onEdit: (res: DictItem) =>
        setPurchaseTradeTerm(
          purchaseTradeTerm.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.PURCHASE_PAY_METHOD]: {
      list: purchasePayMethod,
      placeholder: t("purchase.plPaymentMethod"),
      onDel: (res: string) =>
        setPurchasePayMethod(purchasePayMethod.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setPurchasePayMethod([...purchasePayMethod, res]),
      onEdit: (res: DictItem) =>
        setPurchasePayMethod(
          purchasePayMethod.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.ADDRESS_MANAGE]: {
      list: address,
      placeholder: t("purchase.address"),
      onDel: (res: string) =>
        setAddress(address.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setAddress([...address, res]),
      onEdit: (res: DictItem) =>
        setAddress(
          address.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.EXCHANGE_RATE]: {
      list: exchangeRate,
      placeholder: t("purchase.dictCurrency"),
      onDel: (res: string) =>
        setExchangeRate(exchangeRate.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setExchangeRate([...exchangeRate, res]),
      onEdit: (res: DictItem) =>
        setExchangeRate(
          exchangeRate.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.PRINT_SETTING]: {
      list: printSetting,
      placeholder: t("purchase.printSetting"),
      onDel: (res: string) =>
        setPrintSetting(printSetting.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setPrintSetting([...printSetting, res]),
      onEdit: (res: DictItem) =>
        setPrintSetting(
          printSetting.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.BRAND]: {
      list: brandData,
      placeholder: t("purchase.brand"),
      onDel: (res: string) =>
        setBrandData(brandData.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setBrandData([...brandData, res]),
      onEdit: (res: DictItem) =>
        setBrandData(
          brandData.map((item) => (item.id === res.id ? res : item))
        ),
    },
    [DictEnum.PURCHASE_RULE]: {
      list: purchaseRule,
      placeholder: t("purchase.brand"),
      onDel: (res: string) =>
        setPurchaseRule(purchaseRule.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setPurchaseRule([...purchaseRule, res]),
      onEdit: (res: DictItem) =>
        setPurchaseRule(
          purchaseRule.map((item) => (item.id === res.id ? res : item))
        ),
    },
    // 以下两个字典暂不通过此组件使用，仅预先完善配置
    [DictEnum.PRODUCT_TYPE]: {
      list: productType,
      placeholder: t("purchase.productType"),
      onDel: (res: string) =>
        setProductType(productType.filter((item) => item.id !== res)),
      onAdd: (res: DictItem) => setProductType([...productType, res]),
      onEdit: (res: DictItem) =>
        setProductType(
          productType.map((item) => (item.id === res.id ? res : item))
        ),
    }
  };

  const dictInfo = info[type];

  const realVal = useMemo(() => {
    if (Array.isArray(val)) {
      // 多选值只保留字典中存在的选项，避免表单回填出非字典数据。
      const optionValues = new Set(dictInfo.list?.map(data => onlyText ? data.desc : data.id));
      const matchedValues = val.filter(item => optionValues.has(item));
      return matchedValues.length > 0 ? matchedValues : undefined;
    }

    if (!val) {
      return undefined;
    }

    const hasOption = dictInfo.list?.some(data => onlyText ? data.desc === val : data.id === val);
    return hasOption ? val : undefined;
  }, [dictInfo.list, onlyText, val])

  if (readOnly) {
    const values = Array.isArray(realVal) ? realVal : [realVal];
    const text = values.map(value => {
      const item = dictInfo.list.find(item => onlyText ? item.desc === value : item.id === value);
      return onlyText ? item?.desc : item?.key;
    }).filter(Boolean).join(', ');
    return <SText text={text} style={restProps.style} />;
  }

  return (
    <CellSelectWrap>
      <Tooltip title={
        !readOnly ?
          (realVal ? Array.isArray(realVal) ? realVal.join(", ") : dictInfo.list.find(item => onlyText ? item.desc === realVal : item.id === realVal)?.desc ?? realVal : dictInfo.placeholder) :
          null
      }>
        <Select
          disabled={readOnly}
          value={realVal}
          onChange={onChange}
          style={{ width: "100%" }}
          options={dictInfo.list}
          fieldNames={onlyText ? { label: "desc", value: "desc" } : { label: "key", value: "id" }}
          allowClear
          optionRender={(info) => (
            <Tooltip title={info.data.desc}>
              <DictOption justify="space-between">
                {onlyText ? info.data.desc : info.data.key}
                <Flex gap={4} className="dict-action">
                  <Button
                    type="link"
                    icon={<EditOutlined size={10} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDetail(info.data as DictItem);
                    }}
                  />
                  <SConModal
                    children={
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined size={10} />}
                      />
                    }
                    title={t("common.rmTip")}
                    content={t("common.rmContent")}
                    onOk={() =>
                      del(info.data.id).then(() => {
                        dictInfo.onDel(info.data.id);
                        if (realVal === info.data.id) {
                          setVal(undefined);
                        }
                      })
                    }
                  ></SConModal>
                </Flex>
              </DictOption>
            </Tooltip>
          )}
          placeholder={dictInfo.placeholder}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: "8px 0" }} />
              <Button
                type="text"
                style={{
                  width: "100%",
                  marginBottom: 4,
                  color: colorPrimary,
                }}
                icon={<PlusOutlined />}
                onClick={() => setIsShowAdd(true)}
              >
                {t("common.add")}
              </Button>
            </>
          )}
          {...restProps}
        />
      </Tooltip>
      <DictForm onlyText={onlyText}
        key={"add"}
        open={isShowAdd}
        handleCancel={() => setIsShowAdd(false)}
        handleOk={(data) => new Promise((resolve) => {
          let param: DictInfo = {} as DictInfo;
          if (onlyText) {
            param = {
              ...data,
              key: Date.now() + ''
            }
          } else param = { ...data }
          add(type, param).then((res) => {
            dictInfo.onAdd(res);
            setIsShowAdd(false);
            resolve()
          }).catch((err) => {
            const map: {
              [key in number]: string;
            } = {
              10010: t("common.networkError"),
              70101: t("sale.infoExisted"),
            };
            notification.error({
              placement: 'top',
              message: t("common.addError"),
              description: map[err] ?? t("common.serverError"),
            });
          })
        })}
        data={null}
        leftTitle={t("common.add")}
        confirmLoading={addLoading}
      />
      <DictForm onlyText={onlyText}
        key={detail?.id ?? "detail"}
        open={!!detail}
        handleCancel={() => setDetail(null)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            if (!detail) {
              resolve();
            } else {
              if (onlyText) {
                updateDic(detail.id, { ...data, key: detail.key })
                  .then((res) => {
                    dictInfo.onEdit({ ...res });
                    setDetail(null);
                    resolve();
                  })
                  .catch(() => reject());
              } else update(detail.id, { ...data })
                .then(() => {
                  dictInfo.onEdit({ ...detail, ...data });
                  setDetail(null);
                  resolve();
                })
                .catch(() => reject());
            }
          });
        }}
        data={detail}
        leftTitle={detail?.key + " " + t("common.detail")}
        confirmLoading={upLoading}
      />
    </CellSelectWrap>
  );
};

export default Dict;
